import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import {
  createReportSchedule,
  downloadReportRun,
  generateReport,
  listReportHistory,
  listReportSchedules,
  listReportTemplates,
  runReportSchedule,
  updateReportSchedule,
} from "../api/endpoints/reports"
import { createTarget, listTargets, updateTarget } from "../api/endpoints/targets"
import {
  Button,
  DataTable,
  FormField,
  Input,
  Select,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
  Textarea,
} from "../components/ui"
import { DownloadIcon } from "../components/icons"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDateTime } from "../utils/formatting"

const OPS_VISIBLE_TEMPLATE_KEYS = new Set([
  "CLIENT_PORTFOLIO_SNAPSHOT",
  "RECOVERY_PERFORMANCE",
  "COLLECTIONS_PIPELINE_REPORT",
  "KYC_PIPELINE_REPORT",
  "PAYMENT_BEHAVIOR_REPORT",
  "ESCALATION_FUNNEL_REPORT",
])

const ADMIN_VISIBLE_TEMPLATE_KEYS = new Set([
  "CLIENT_PORTFOLIO_SNAPSHOT",
  "RECOVERY_PERFORMANCE",
  "COLLECTIONS_PIPELINE_REPORT",
  "TEAM_TARGET_MATRIX",
  "AGENT_ACTIVITY_REPORT",
  "UNASSIGNED_CLIENTS_REPORT",
  "KYC_PIPELINE_REPORT",
])

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
      <div className="mb-4">
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        {description ? <p className="text-sm text-slate-500 mt-1">{description}</p> : null}
      </div>
      {children}
    </section>
  )
}

function parseEmailList(value: string) {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
}

export function ReportsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<"reports" | "targets">("reports")
  const [recipientEmails, setRecipientEmails] = useState("")
  const [scheduleEmails, setScheduleEmails] = useState("")
  const [targetNotes, setTargetNotes] = useState("")

  const [generateForm, setGenerateForm] = useState({
    templateKey: "",
    format: "pdf",
    delivery: "download",
  })
  const [scheduleForm, setScheduleForm] = useState({
    name: "",
    templateKey: "",
    format: "pdf",
    delivery: "email",
    frequency: "WEEKLY",
    runHourUtc: "6",
    runMinuteUtc: "0",
    runDayOfWeek: "1",
    runDayOfMonth: "1",
  })
  const [targetForm, setTargetForm] = useState({
    name: "",
    scopeType: "TEAM",
    agentId: "",
    teamName: user?.teamName ?? "",
    targetPercentage: "75",
    timeframe: "MONTHLY",
    overdueCategory: "CATEGORY_A",
    thresholdGreen: "0.8",
    thresholdYellow: "0.5",
  })

  if (!user) {
    return null
  }

  const canManageTargets = Boolean(user.permissions?.canManageTargets)
  const canManageTargetsOnReports = user.role === "SUPER_ADMIN"
  const canManageSchedules = Boolean(user.permissions?.canManageReportSchedules)
  const isOpsManager = user.role === "OPS_MANAGER"
  const isAdmin = user.role === "ADMIN"

  const templatesQuery = useQuery({
    queryKey: ["reports-templates"],
    queryFn: () => listReportTemplates(),
  })
  const historyQuery = useQuery({
    queryKey: ["reports-history"],
    queryFn: () => listReportHistory({ per_page: "20" }),
  })
  const schedulesQuery = useQuery({
    queryKey: ["reports-schedules"],
    queryFn: () => listReportSchedules({ per_page: "20" }),
  })
  const targetsQuery = useQuery({
    queryKey: ["targets"],
    queryFn: () => listTargets({ per_page: "20" }),
    enabled: canManageTargetsOnReports,
  })

  const generateMutation = useMutation({
    mutationFn: () =>
      generateReport({
        templateKey:
          generateForm.templateKey || visibleTemplates[0]?.templateKey || templatesQuery.data?.data?.[0]?.templateKey,
        format: generateForm.format,
        delivery: generateForm.delivery,
        recipientEmails: parseEmailList(recipientEmails),
        filters: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports-history"] })
    },
  })

  const scheduleMutation = useMutation({
    mutationFn: () =>
      createReportSchedule({
        name: scheduleForm.name,
        templateKey:
          scheduleForm.templateKey ||
          visibleTemplates[0]?.templateKey ||
          templatesQuery.data?.data?.[0]?.templateKey,
        format: scheduleForm.format,
        delivery: scheduleForm.delivery,
        frequency: scheduleForm.frequency,
        runHourUtc: Number(scheduleForm.runHourUtc),
        runMinuteUtc: Number(scheduleForm.runMinuteUtc),
        runDayOfWeek: scheduleForm.frequency === "WEEKLY" ? Number(scheduleForm.runDayOfWeek) : undefined,
        runDayOfMonth: scheduleForm.frequency === "MONTHLY" ? Number(scheduleForm.runDayOfMonth) : undefined,
        recipientEmails: parseEmailList(scheduleEmails),
        filters: {},
        enabled: true,
      }),
    onSuccess: () => {
      setScheduleForm((current) => ({ ...current, name: "" }))
      setScheduleEmails("")
      void queryClient.invalidateQueries({ queryKey: ["reports-schedules"] })
    },
  })

  const runScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) => runReportSchedule(scheduleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports-history"] })
      void queryClient.invalidateQueries({ queryKey: ["reports-schedules"] })
    },
  })

  const toggleScheduleMutation = useMutation({
    mutationFn: ({ scheduleId, enabled }: { scheduleId: string; enabled: boolean }) =>
      updateReportSchedule(scheduleId, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports-schedules"] })
    },
  })

  const createTargetMutation = useMutation({
    mutationFn: () =>
      createTarget({
        name: targetForm.name,
        scopeType: targetForm.scopeType,
        agentId: targetForm.scopeType === "AGENT" ? targetForm.agentId : undefined,
        teamName: targetForm.scopeType === "TEAM" ? targetForm.teamName : undefined,
        targetPercentage: Number(targetForm.targetPercentage),
        timeframe: targetForm.timeframe,
        overdueCategory: targetForm.overdueCategory,
        thresholdGreen: Number(targetForm.thresholdGreen),
        thresholdYellow: Number(targetForm.thresholdYellow),
        notes: targetNotes,
      }),
    onSuccess: () => {
      setTargetForm((current) => ({ ...current, name: "" }))
      setTargetNotes("")
      void queryClient.invalidateQueries({ queryKey: ["targets"] })
    },
  })

  const toggleTargetMutation = useMutation({
    mutationFn: ({ targetId, isActive }: { targetId: string; isActive: boolean }) =>
      updateTarget(targetId, { isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["targets"] })
    },
  })

  const templates = templatesQuery.data?.data ?? []
  const visibleTemplates = isOpsManager
    ? templates.filter((template: any) => OPS_VISIBLE_TEMPLATE_KEYS.has(template.templateKey))
    : isAdmin
      ? templates.filter((template: any) => ADMIN_VISIBLE_TEMPLATE_KEYS.has(template.templateKey))
    : templates
  const history = historyQuery.data?.data ?? []
  const schedules = schedulesQuery.data?.data ?? []
  const targets = targetsQuery.data?.data ?? []

  return (
    <div className="space-y-6">
      <section className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {canManageTargetsOnReports ? "Reports and targets" : "Reports"}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {canManageTargetsOnReports
              ? "Generate exports asynchronously, track report history, and manage recurring schedules."
              : "Generate monitoring exports, review report history, and inspect recurring schedules."}
          </p>
        </div>
        {canManageTargetsOnReports ? (
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1">
            <button
              type="button"
              className={`px-4 py-2 text-sm rounded-md ${activeTab === "reports" ? "bg-slate-900 text-white" : "text-slate-600"}`}
              onClick={() => setActiveTab("reports")}
            >
              Reports
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm rounded-md ${activeTab === "targets" ? "bg-slate-900 text-white" : "text-slate-600"}`}
              onClick={() => setActiveTab("targets")}
            >
              Targets
            </button>
          </div>
        ) : null}
      </section>

      {activeTab === "reports" ? (
        <>
          <Section
            title="Generate Report"
            description="All exports create report runs first, then become downloadable from history."
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <FormField label="Template">
                <Select
                  value={generateForm.templateKey || visibleTemplates[0]?.templateKey || ""}
                  onChange={(event) =>
                    setGenerateForm((current) => ({ ...current, templateKey: event.target.value }))
                  }
                >
                  {visibleTemplates.map((template: any) => (
                    <option key={template.templateKey} value={template.templateKey}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Format">
                <Select
                  value={generateForm.format}
                  onChange={(event) =>
                    setGenerateForm((current) => ({ ...current, format: event.target.value }))
                  }
                >
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                </Select>
              </FormField>
              <FormField label="Delivery">
                <Select
                  value={generateForm.delivery}
                  onChange={(event) =>
                    setGenerateForm((current) => ({ ...current, delivery: event.target.value }))
                  }
                >
                  <option value="download">Download</option>
                  <option value="email">Email</option>
                </Select>
              </FormField>
              <FormField label="Recipient Emails">
                <Input
                  placeholder="ops@example.com, audit@example.com"
                  value={recipientEmails}
                  onChange={(event) => setRecipientEmails(event.target.value)}
                />
              </FormField>
            </div>
            <div className="mt-4">
              <Button onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
                Generate Report
              </Button>
            </div>
          </Section>

          {canManageSchedules ? (
            <Section title="Schedule Report" description="Recurring schedules create the same report runs as ad hoc exports.">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField label="Schedule Name">
                  <Input
                    value={scheduleForm.name}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, name: event.target.value }))
                    }
                  />
                </FormField>
                <FormField label="Template">
                  <Select
                    value={scheduleForm.templateKey || visibleTemplates[0]?.templateKey || ""}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, templateKey: event.target.value }))
                    }
                  >
                    {visibleTemplates.map((template: any) => (
                      <option key={template.templateKey} value={template.templateKey}>
                        {template.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Frequency">
                  <Select
                    value={scheduleForm.frequency}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, frequency: event.target.value }))
                    }
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                  </Select>
                </FormField>
                <FormField label="Hour UTC">
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={scheduleForm.runHourUtc}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, runHourUtc: event.target.value }))
                    }
                  />
                </FormField>
                <FormField label="Minute UTC">
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    value={scheduleForm.runMinuteUtc}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, runMinuteUtc: event.target.value }))
                    }
                  />
                </FormField>
                {scheduleForm.frequency === "WEEKLY" ? (
                  <FormField label="Day Of Week">
                    <Input
                      type="number"
                      min={0}
                      max={6}
                      value={scheduleForm.runDayOfWeek}
                      onChange={(event) =>
                        setScheduleForm((current) => ({ ...current, runDayOfWeek: event.target.value }))
                      }
                    />
                  </FormField>
                ) : null}
                {scheduleForm.frequency === "MONTHLY" ? (
                  <FormField label="Day Of Month">
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      value={scheduleForm.runDayOfMonth}
                      onChange={(event) =>
                        setScheduleForm((current) => ({ ...current, runDayOfMonth: event.target.value }))
                      }
                    />
                  </FormField>
                ) : null}
                <FormField label="Format">
                  <Select
                    value={scheduleForm.format}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, format: event.target.value }))
                    }
                  >
                    <option value="pdf">PDF</option>
                    <option value="csv">CSV</option>
                  </Select>
                </FormField>
                <FormField label="Delivery">
                  <Select
                    value={scheduleForm.delivery}
                    onChange={(event) =>
                      setScheduleForm((current) => ({ ...current, delivery: event.target.value }))
                    }
                  >
                    <option value="email">Email</option>
                    <option value="download">Download</option>
                  </Select>
                </FormField>
                <FormField label="Recipient Emails">
                  <Input
                    placeholder="ops@example.com, audit@example.com"
                    value={scheduleEmails}
                    onChange={(event) => setScheduleEmails(event.target.value)}
                  />
                </FormField>
              </div>
              <div className="mt-4">
                <Button onClick={() => scheduleMutation.mutate()} disabled={scheduleMutation.isPending}>
                  Create Schedule
                </Button>
              </div>
            </Section>
          ) : null}

          <Section title="Report History">
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Run</Th>
                  <Th>Template</Th>
                  <Th>Status</Th>
                  <Th>Delivery</Th>
                  <Th>Created</Th>
                  <Th className="text-right">Action</Th>
                </TableHead>
                <TableBody>
                  {history.map((run: any) => (
                    <TableRow key={run.runId}>
                      <Td>
                        <div className="font-semibold text-slate-800">{run.runId}</div>
                        <div className="text-[11px] text-slate-500">{run.requestedByName ?? "-"}</div>
                      </Td>
                      <Td>{run.templateName}</Td>
                      <Td>{run.status}</Td>
                      <Td>{run.delivery}</Td>
                      <Td>{formatDateTime(run.createdAt)}</Td>
                      <Td className="text-right">
                        {run.hasFile ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void downloadReportRun(run.runId)}
                          >
                            <DownloadIcon className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Waiting</span>
                        )}
                      </Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          </Section>

          <Section title="Schedules" description="Ops Managers can inspect schedules, while Super/Admin can change them.">
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Name</Th>
                  <Th>Template</Th>
                  <Th>Next Run</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Action</Th>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule: any) => (
                    <TableRow key={schedule.scheduleId}>
                      <Td>
                        <div className="font-semibold text-slate-800">{schedule.name}</div>
                        <div className="text-[11px] text-slate-500">{schedule.scheduleId}</div>
                      </Td>
                      <Td>{schedule.templateName}</Td>
                      <Td>{schedule.nextRunAt ? formatDateTime(schedule.nextRunAt) : "-"}</Td>
                      <Td>{schedule.enabled ? "Enabled" : "Paused"}</Td>
                      <Td className="text-right">
                        <div className="flex justify-end gap-2">
                          {canManageSchedules ? (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => runScheduleMutation.mutate(schedule.scheduleId)}
                              >
                                Run Now
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  toggleScheduleMutation.mutate({
                                    scheduleId: schedule.scheduleId,
                                    enabled: !schedule.enabled,
                                  })
                                }
                              >
                                {schedule.enabled ? "Pause" : "Enable"}
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400">Read only</span>
                          )}
                        </div>
                      </Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          </Section>
        </>
      ) : (
        <>
          <Section title="Create Target" description="Target scope stays template-driven: agent, team, or all agents.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormField label="Target Name">
                <Input
                  value={targetForm.name}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </FormField>
              <FormField label="Scope">
                <Select
                  value={targetForm.scopeType}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, scopeType: event.target.value }))
                  }
                >
                  <option value="AGENT">Agent</option>
                  <option value="TEAM">Team</option>
                  <option value="ALL_AGENTS">All Agents</option>
                </Select>
              </FormField>
              <FormField label="Timeframe">
                <Select
                  value={targetForm.timeframe}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, timeframe: event.target.value }))
                  }
                >
                  <option value="WEEKLY">Weekly</option>
                  <option value="BI_WEEKLY">Bi-weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="CUSTOM">Custom</option>
                </Select>
              </FormField>
              {targetForm.scopeType === "AGENT" ? (
                <FormField label="Agent Public ID">
                  <Input
                    value={targetForm.agentId}
                    onChange={(event) =>
                      setTargetForm((current) => ({ ...current, agentId: event.target.value }))
                    }
                  />
                </FormField>
              ) : null}
              {targetForm.scopeType === "TEAM" ? (
                <FormField label="Team Name">
                  <Input
                    value={targetForm.teamName}
                    onChange={(event) =>
                      setTargetForm((current) => ({ ...current, teamName: event.target.value }))
                    }
                  />
                </FormField>
              ) : null}
              <FormField label="Target %">
                <Input
                  type="number"
                  value={targetForm.targetPercentage}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, targetPercentage: event.target.value }))
                  }
                />
              </FormField>
              <FormField label="Overdue Category">
                <Select
                  value={targetForm.overdueCategory}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, overdueCategory: event.target.value }))
                  }
                >
                  <option value="S1">S1</option>
                  <option value="CATEGORY_A">Category A</option>
                  <option value="CATEGORY_B">Category B</option>
                  <option value="CATEGORY_C">Category C</option>
                  <option value="CATEGORY_Z">Category Z</option>
                </Select>
              </FormField>
              <FormField label="Green Threshold">
                <Input
                  type="number"
                  step="0.01"
                  value={targetForm.thresholdGreen}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, thresholdGreen: event.target.value }))
                  }
                />
              </FormField>
              <FormField label="Yellow Threshold">
                <Input
                  type="number"
                  step="0.01"
                  value={targetForm.thresholdYellow}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, thresholdYellow: event.target.value }))
                  }
                />
              </FormField>
              <FormField label="Notes" className="md:col-span-3">
                <Textarea value={targetNotes} onChange={(event) => setTargetNotes(event.target.value)} />
              </FormField>
            </div>
            <div className="mt-4">
              <Button onClick={() => createTargetMutation.mutate()} disabled={createTargetMutation.isPending}>
                Create Target
              </Button>
            </div>
          </Section>

          <Section title="Target Registry">
            <div className="overflow-x-auto">
              <DataTable>
                <TableHead>
                  <Th>Target</Th>
                  <Th>Scope</Th>
                  <Th>Progress</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Action</Th>
                </TableHead>
                <TableBody>
                  {targets.map((target: any) => (
                    <TableRow key={target.targetId}>
                      <Td>
                        <div className="font-semibold text-slate-800">{target.name}</div>
                        <div className="text-[11px] text-slate-500">{target.targetId}</div>
                      </Td>
                      <Td>{target.scopeType}</Td>
                      <Td>
                        {target.progress
                          ? `${target.progress.actualPercentage}% / ${target.targetPercentage}%`
                          : "-"}
                      </Td>
                      <Td>{target.progress?.status ?? (target.isActive ? "ACTIVE" : "INACTIVE")}</Td>
                      <Td className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            toggleTargetMutation.mutate({
                              targetId: target.targetId,
                              isActive: !target.isActive,
                            })
                          }
                        >
                          {target.isActive ? "Deactivate" : "Activate"}
                        </Button>
                      </Td>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </div>
          </Section>
        </>
      )}

      {generateMutation.data?.data ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 p-4 text-sm">
          Report run {generateMutation.data.data.runId} is queued. It will appear in history once processing starts.
        </div>
      ) : null}
    </div>
  )
}
